import { createColumnHelper } from '@tanstack/react-table';
import type { SortingFn } from '@tanstack/react-table';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { ExclamationTriangleIcon, ClockIcon, EyeIcon } from '@heroicons/react/24/outline';
import { VacationAvatar } from './components/VacationAvatar';
import { VacationDpPeriodCell } from './components/VacationDpPeriodCell';
import {
    getCollaboratorProfileHref,
    getRequestStatusMetadata,
    getReceiptPendingLabel,
} from '../../vacations.definitions';
import { compareVacationUrgency, getStatusRank } from '../../utils/vacationDpUrgency';
import { resolveDpDeadline } from '../../utils/vacationDpDeadline';
import { resolveDpDays } from '../../utils/vacationDpDays';
import { resolveApprovedPeriodCells } from '../../utils/vacationDpPeriods';
import type { VacationDpRow } from '../../models/vacationDpBoard.types';
import {
    CELL_TONE_CLASSES,
    COLUMN_LABELS,
    NOT_APPLICABLE,
    NO_CONFLICT,
    NO_REQUEST_ACTION_TITLE,
    NO_REQUEST_LABEL,
    OPEN_REQUEST_LABEL,
    SELL_SHORT_LABELS,
    UNALLOCATED_WARNING_LABEL,
    getColumnLabel,
} from './vacationDpTable.definitions';
import type { CellTone } from './vacationDpTable.types';
import type { DpColumnProfile } from './vacationDpTable.definitions';
const columnHelper = createColumnHelper<VacationDpRow>();
function Cell({ tone = 'none', children }: { tone?: CellTone; children: React.ReactNode }) {
    if (tone === 'none') {
        return <span className="font-montserrat">{children}</span>;
    }
    if (tone === 'muted') {
        return <span className="font-montserrat text-raro-gray-400">{children}</span>;
    }
    return (
        <span
            className={clsx(
                'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium font-montserrat',
                CELL_TONE_CLASSES[tone]
            )}
        >
            {children}
        </span>
    );
}
export function buildVacationDpColumns(
    onOpenRequest: (row: VacationDpRow) => void,
    today: Date,
    profile: DpColumnProfile = 'general',
    onOpenHistory?: (collaborator: { id: string; name: string; email: string }) => void
) {
    const urgencySort: SortingFn<VacationDpRow> = (rowA, rowB) =>
        compareVacationUrgency(rowA.original, rowB.original, today);
    const daysLabel = getColumnLabel(profile, 'days');
    const deadlineLabel = getColumnLabel(profile, 'deadline');
    const period1Label = getColumnLabel(profile, 'period1');
    const period2Label = getColumnLabel(profile, 'period2');
    return [
        columnHelper.display({
            id: 'actions',
            header: COLUMN_LABELS.actions,
            size: 100,
            meta: { headerAlign: 'center' as const },
            cell: (info) => {
                const row = info.row.original;
                const disabled = row.request === null;
                return (
                    <div className="flex items-center justify-center gap-2">
                        <button
                            type="button"
                            disabled={disabled}
                            title={disabled ? NO_REQUEST_ACTION_TITLE : OPEN_REQUEST_LABEL}
                            aria-label={OPEN_REQUEST_LABEL}
                            onClick={() => onOpenRequest(row)}
                            className="rounded-lg border border-primary-200 p-1.5 text-primary-700 transition-colors hover:border-primary-300 hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-transparent disabled:text-raro-gray-300 disabled:hover:bg-transparent"
                        >
                            <EyeIcon className="h-4 w-4" />
                        </button>
                        {onOpenHistory && (
                            <button
                                type="button"
                                title="Ver histórico"
                                aria-label="Ver histórico de férias"
                                onClick={() =>
                                    onOpenHistory({
                                        id: row.collaboratorId,
                                        name: row.collaboratorName,
                                        email: row.collaboratorEmail,
                                    })
                                }
                                className="rounded-lg border border-gray-200 p-1.5 text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50"
                            >
                                <ClockIcon className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                );
            },
        }),
        columnHelper.accessor('collaboratorName', {
            id: 'collaborator',
            header: COLUMN_LABELS.collaborator,
            size: 260,
            cell: (info) => (
                <div className="flex items-center gap-2.5">
                    <VacationAvatar
                        name={info.getValue()}
                        photoUrl={info.row.original.collaboratorPhotoUrl}
                    />
                    <Link
                        to={getCollaboratorProfileHref(info.row.original.collaboratorId)}
                        className="truncate font-medium text-raro-gray-900 underline-offset-2 transition-colors hover:text-primary-700 hover:underline font-montserrat"
                    >
                        {info.getValue()}
                    </Link>
                </div>
            ),
            sortingFn: (a, b) =>
                a.original.collaboratorName.localeCompare(b.original.collaboratorName, 'pt-BR'),
        }),
        columnHelper.accessor((row) => resolveDpDays(row).value, {
            id: 'days',
            header: daysLabel,
            size: 140,
            meta: { align: 'left' as const },
            cell: (info) => {
                const row = info.row.original;
                const daysInfo = resolveDpDays(row);
                const isExpired = daysInfo.kind === 'expired';
                const valueText = isExpired ? `${info.getValue()}d vencidos` : `${info.getValue()}d`;
                const valueClass = isExpired ? 'text-red-700 font-semibold' : 'text-raro-gray-900';
                return (
                    <div className="space-y-1">
                        <div className="flex items-center justify-end gap-1">
                            <span className={clsx('tabular-nums font-medium font-montserrat', valueClass)}>
                                {valueText}
                            </span>
                            {row.unallocatedDays > 0 && (
                                <div title={UNALLOCATED_WARNING_LABEL}>
                                    <ExclamationTriangleIcon
                                        className="h-4 w-4 text-amber-500"
                                        aria-hidden="true"
                                    />
                                    <span className="sr-only">{UNALLOCATED_WARNING_LABEL}</span>
                                </div>
                            )}
                        </div>
                        {daysInfo.hint && (
                            <div className="tabular-nums text-xs text-raro-gray-500 font-montserrat">
                                {daysInfo.hint}
                            </div>
                        )}
                    </div>
                );
            },
        }),
        columnHelper.accessor((row) => row.concessiveDeadline, {
            id: 'deadline',
            header: deadlineLabel,
            size: 210,
            cell: (info) => {
                const row = info.row.original;
                const deadline = resolveDpDeadline(row, today);
                return (
                    <div className="space-y-0.5">
                        <Cell tone={deadline.tone as CellTone}>
                            {deadline.label}
                        </Cell>
                        {deadline.hint && (
                            <span className="block px-2 text-xs text-raro-gray-500 font-montserrat">
                                {deadline.hint}
                            </span>
                        )}
                    </div>
                );
            },
            sortingFn: urgencySort,
        }),
        columnHelper.display({
            id: 'period1',
            header: period1Label,
            size: 210,
            cell: (info) => {
                const row = info.row.original;
                const [period1] = resolveApprovedPeriodCells(row, today);
                return <VacationDpPeriodCell cell={period1} />;
            },
        }),
        columnHelper.display({
            id: 'period2',
            header: period2Label,
            size: 210,
            cell: (info) => {
                const row = info.row.original;
                const [, period2] = resolveApprovedPeriodCells(row, today);
                return <VacationDpPeriodCell cell={period2} />;
            },
        }),
        columnHelper.accessor((row) => row.request?.status ?? null, {
            id: 'stage',
            header: COLUMN_LABELS.stage,
            size: 200,
            cell: (info) => {
                const request = info.row.original.request;
                if (!request) return <Cell tone="muted">{NO_REQUEST_LABEL}</Cell>;
                const metadata = getRequestStatusMetadata(request);
                const receiptPendingLabel = getReceiptPendingLabel(request);
                return (
                    <div className="flex items-center gap-1">
                        <span
                            className={clsx(
                                'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium font-montserrat',
                                metadata.className
                            )}
                        >
                            {metadata.label}
                        </span>
                        {receiptPendingLabel && (
                            <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-1 text-xs font-medium text-amber-800 font-montserrat">
                                {receiptPendingLabel}
                            </span>
                        )}
                    </div>
                );
            },
            enableSorting: true,
            sortingFn: (a, b) => getStatusRank(a.original) - getStatusRank(b.original),
        }),
        columnHelper.display({
            id: 'sell',
            header: COLUMN_LABELS.sell,
            size: 130,
            cell: (info) => {
                const sell = info.row.original.request?.sellRequest;
                if (!sell || !sell.requested) return <Cell tone="muted">{NOT_APPLICABLE}</Cell>;
                return (
                    <Cell tone={sell.status === 'Pending' ? 'primary' : 'none'}>
                        {SELL_SHORT_LABELS[sell.status] ?? sell.status}
                    </Cell>
                );
            },
        }),
        columnHelper.display({
            id: 'conflict',
            header: COLUMN_LABELS.conflict,
            size: 150,
            cell: (info) => {
                const request = info.row.original.request;
                if (!request) return <Cell tone="muted">{NOT_APPLICABLE}</Cell>;
                const conflicts = request.conflictsSnapshot;
                if (conflicts.length === 0) return <Cell>{NO_CONFLICT}</Cell>;
                const first = conflicts[0];
                const label = first.projectName ?? first.clientName ?? 'Alocação ativa';
                const extra = conflicts.length > 1 ? ` +${conflicts.length - 1}` : '';
                return <Cell tone="warning">{`${label}${extra}`}</Cell>;
            },
        }),
    ];
}
